import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useQuery } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import {
  Alert,
  Box,
  Card,
  Chip,
  CircularProgress,
  Grid,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import { queryKeys } from "src/react-query/queryKeys";
import { fetchWithError } from "src/react-query/fetchWithError";
import { fUTCToLocal } from "src/utils/formatTime";
import { hideColumnsOnPhone } from "./tableSx";
import { VehicleLink, ChargerLink } from "./DetailLinks";

// Readiness: is each truck charged enough to go out? Every registered battery's
// latest SoC (live while charging, else the stop SoC of its last session) against
// the company's "ready at" SoC. A truck is only as ready as its weakest battery.

const REFRESH_MS = 60000;
// 5 rows per page keeps the card compact on the dashboard.
const ROWS_PER_PAGE_OPTIONS = [5, 10, 25, 50];
// Problem trucks first.
const STATUS_ORDER = { low: 0, unknown: 1, charging: 2, ready: 3 };

const Tile = styled(Card, {
  shouldForwardProp: (prop) => prop !== "accent" && prop !== "active",
})(({ theme, accent, active }) => ({
  padding: theme.spacing(1.75),
  borderRadius: theme.shape.borderRadiusMd,
  border: `1px solid ${active ? accent : theme.palette.grey[200]}`,
  borderTop: `3px solid ${accent}`,
  background: active ? `${accent}12` : theme.palette.background.paper,
  boxShadow: theme.customShadows.z1,
  height: "100%",
  cursor: "pointer",
  "&:focus-visible": { outline: `2px solid ${accent}`, outlineOffset: 2 },
}));

const statusMeta = (theme) => ({
  ready: { label: "Ready", color: theme.palette.success.main, chip: "success" },
  charging: { label: "Charging", color: theme.palette.info.main, chip: "info" },
  low: { label: "Low", color: theme.palette.error.main, chip: "error" },
  unknown: { label: "No SoC", color: theme.palette.text.secondary, chip: "default" },
});

const lastSeen = (utc) => {
  const d = fUTCToLocal(utc);
  return d ? `${formatDistanceToNow(d)} ago` : "Never charged";
};

const BatteryCell = ({ battery, readySocPct }) => {
  const theme = useTheme();
  if (!battery) return <>—</>;
  const meta = statusMeta(theme)[battery.status];
  const pct = battery.soc == null ? 0 : Math.min(100, Math.max(0, battery.soc));
  return (
    <Tooltip
      title={[
        battery.mac && `MAC ${battery.mac}`,
        battery.siteName && `Last charged at ${battery.siteName}`,
        battery.chargerName || battery.chargerId,
      ]
        .filter(Boolean)
        .join(" · ")}
    >
      <Stack spacing={0.5} sx={{ minWidth: 96, cursor: "help" }}>
        <Typography variant="body2" sx={{ fontWeight: 600, color: meta.color }}>
          {battery.soc == null ? "—" : `${Math.round(battery.soc)}%`}
          {battery.status === "charging" ? " ⚡" : ""}
        </Typography>
        <Box sx={{ position: "relative" }}>
          <LinearProgress
            variant="determinate"
            value={pct}
            sx={{
              height: 5,
              borderRadius: 3,
              bgcolor: theme.palette.grey[200],
              "& .MuiLinearProgress-bar": { backgroundColor: meta.color },
            }}
          />
          {/* The "ready at" mark on the bar. */}
          <Box
            sx={{
              position: "absolute",
              top: -2,
              bottom: -2,
              left: `${readySocPct}%`,
              width: "2px",
              bgcolor: "text.secondary",
            }}
          />
        </Box>
      </Stack>
    </Tooltip>
  );
};
BatteryCell.propTypes = { battery: PropTypes.object, readySocPct: PropTypes.number.isRequired };

const VehicleReadinessPanel = ({ teamId, siteId = null }) => {
  const theme = useTheme();
  const META = statusMeta(theme);
  const [filter, setFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(ROWS_PER_PAGE_OPTIONS[0]);

  const { data, isLoading, error } = useQuery(
    queryKeys.vehicleChargingReadiness(teamId, siteId ? { siteId } : undefined),
    fetchWithError,
    { enabled: Boolean(teamId), refetchInterval: REFRESH_MS, select: (r) => r?.data ?? null }
  );
  const readySocPct = Number(data?.readySocPct) || 80;
  const summary = data?.summary || { ready: 0, charging: 0, low: 0, unknown: 0, vehicles: 0 };

  const vehicles = useMemo(() => {
    const list = data?.vehicles || [];
    const shown = filter === "all" ? list : list.filter((v) => v.status === filter);
    return [...shown].sort(
      (a, b) =>
        STATUS_ORDER[a.status] - STATUS_ORDER[b.status] ||
        String(a.registrationNumber || "").localeCompare(String(b.registrationNumber || ""))
    );
  }, [data, filter]);
  const safePage = Math.min(page, Math.max(0, Math.ceil(vehicles.length / rowsPerPage) - 1));
  const visible = vehicles.slice(safePage * rowsPerPage, (safePage + 1) * rowsPerPage);

  const tiles = [
    { key: "ready", hint: `Every battery at or above ${readySocPct}%` },
    { key: "charging", hint: "A battery is charging now" },
    { key: "low", hint: `A battery below ${readySocPct}% and not charging` },
    { key: "unknown", hint: "Never charged, or the charger reported no SoC" },
  ];
  const select = (key) => {
    setFilter((f) => (f === key ? "all" : key));
    setPage(0);
  };

  if (error) {
    return <Alert severity="error">Couldn&apos;t load vehicle readiness: {error.message}</Alert>;
  }

  return (
    <>
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        {tiles.map((t) => (
          <Grid item xs={6} sm={3} key={t.key}>
            <Tooltip title={t.hint} arrow placement="top">
              <Tile
                accent={META[t.key].color}
                active={filter === t.key}
                role="button"
                tabIndex={0}
                aria-pressed={filter === t.key}
                onClick={() => select(t.key)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    select(t.key);
                  }
                }}
              >
                <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 500 }}>
                  {META[t.key].label}
                </Typography>
                {isLoading ? (
                  <Box sx={{ py: 0.5 }}>
                    <CircularProgress size={20} />
                  </Box>
                ) : (
                  <Typography variant="h5" sx={{ fontWeight: 700, color: META[t.key].color }}>
                    {summary[t.key]}
                  </Typography>
                )}
              </Tile>
            </Tooltip>
          </Grid>
        ))}
      </Grid>
      <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 1 }}>
        Ready at {readySocPct}% SoC (Company → Settings → Fleet). Select a tile to filter; the mark on each
        bar is the ready level.
      </Typography>

      <TableContainer>
        <Table size="small" sx={hideColumnsOnPhone([5, 6])}>
          <TableHead>
            <TableRow>
              <TableCell>Vehicle</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Battery 1</TableCell>
              <TableCell>Battery 2</TableCell>
              <TableCell>Last Charged</TableCell>
              <TableCell>Site</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={26} />
                </TableCell>
              </TableRow>
            )}
            {!isLoading && vehicles.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4, color: "text.secondary" }}>
                  {summary.vehicles === 0
                    ? "No vehicles with battery MACs yet. Add them on each vehicle's page."
                    : "No vehicles in this state."}
                </TableCell>
              </TableRow>
            )}
            {!isLoading &&
              visible.map((v) => {
                const bySlot = Object.fromEntries(v.batteries.map((b) => [b.slot, b]));
                const latest = v.batteries
                  .map((b) => b.lastSessionAt)
                  .filter(Boolean)
                  .sort()
                  .pop();
                const latestBattery = v.batteries.find((b) => b.lastSessionAt === latest);
                return (
                  <TableRow key={v.vehicleId} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        <VehicleLink vehicleId={v.vehicleId}>{v.registrationNumber || `Vehicle ${v.vehicleId}`}</VehicleLink>
                      </Typography>
                      <Typography variant="caption" sx={{ color: "text.secondary" }}>
                        {[v.make, v.model].filter(Boolean).join(" ")}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" color={META[v.status].chip} variant="outlined" label={META[v.status].label} />
                    </TableCell>
                    <TableCell>
                      <BatteryCell battery={bySlot[1]} readySocPct={readySocPct} />
                    </TableCell>
                    <TableCell>
                      <BatteryCell battery={bySlot[2]} readySocPct={readySocPct} />
                    </TableCell>
                    <TableCell>
                      {lastSeen(latest)}
                      {latestBattery?.chargerId && (
                        <Typography variant="caption" sx={{ display: "block", color: "text.secondary" }}>
                          at <ChargerLink chargerPk={latestBattery.chargerPk}>{latestBattery.chargerId}</ChargerLink>
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{latestBattery?.siteName || "—"}</TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>
      {!isLoading && vehicles.length > rowsPerPage && (
        <TablePagination
          component="div"
          count={vehicles.length}
          page={safePage}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
        />
      )}
    </>
  );
};

VehicleReadinessPanel.propTypes = {
  teamId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  siteId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default VehicleReadinessPanel;
